import { useMemo, useState } from "react";
import { Package, Plus, Search } from "lucide-react";
import { ActionMenu } from "../../components/ui/action-menu";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import { useToast } from "../../components/ui/toast";
import { ApiV2Error } from "../../lib/api-v2";
import { ProductFormModal } from "./product-form-modal";
import { useAdminProductMutations, useAdminProducts } from "./queries";
import { amountRange, isPlan, rupees, type Product, type ProductInput } from "./types";

const errorMessage = (err: unknown, fallback: string) => (err instanceof ApiV2Error ? err.message : fallback);

/** /admin/products: TechLadder admins create, edit and delete the shared catalog. */
export function AdminProductsPage() {
  const toast = useToast();
  const products = useAdminProducts();
  const m = useAdminProductMutations();
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState<
    | { kind: "create" }
    | { kind: "edit"; product: Product }
    | { kind: "delete"; product: Product }
    | null
  >(null);

  const all = useMemo(() => products.data?.pages.flatMap((p) => p.items) ?? [], [products.data]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? all.filter((p) => p.name.toLowerCase().includes(q)) : all;
  }, [all, search]);

  const create = async (input: ProductInput) => {
    const product = await m.create.mutateAsync(input);
    toast.success("Product created", product.name);
    setDialog(null);
  };

  const update = async (product: Product, input: ProductInput) => {
    // Send only what changed; the API rejects an empty update.
    const changes: Partial<ProductInput> = {};
    if (input.name !== product.name) changes.name = input.name;
    if (input.price !== product.price) changes.price = input.price;
    if (input.description !== product.description) changes.description = input.description;
    if (input.min_amount !== product.min_amount) changes.min_amount = input.min_amount;
    if (input.max_amount !== product.max_amount) changes.max_amount = input.max_amount;
    if (Object.keys(changes).length) {
      const saved = await m.update.mutateAsync({ id: product.id, changes });
      toast.success("Product updated", saved.name);
    }
    setDialog(null);
  };

  const remove = (product: Product) => {
    setDialog(null);
    m.remove.mutate(product.id, {
      onSuccess: () => toast.danger("Product deleted", product.name),
      onError: (err) => toast.danger("Could not delete product", errorMessage(err, "Try again.")),
    });
  };

  return (
    <div className="space-y-6 p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-ink">Products</h1>
          <p className="mt-1 text-sm text-ink-2">
            The catalog every account sees once Products is turned on for it in User Access.
          </p>
        </div>
        <button
          onClick={() => setDialog({ kind: "create" })}
          className="flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover"
        >
          <Plus className="h-4 w-4" /> New Product
        </button>
      </div>

      <div className="rounded-xl border border-line bg-elevated shadow-sm">
        <div className="flex items-center gap-3 border-b border-line p-4">
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products"
              className="w-full rounded-lg border border-line py-2 pl-9 pr-3 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            />
          </div>
          <span className="ml-auto font-mono text-xs text-ink-3">
            {all.length} product{all.length === 1 ? "" : "s"}{products.hasNextPage ? "+" : ""}
          </span>
        </div>

        {products.isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-soft" />)}
          </div>
        ) : products.isError ? (
          <div className="flex flex-col items-center gap-3 p-10 text-center">
            <p className="text-sm text-danger">{errorMessage(products.error, "Could not load products.")}</p>
            <button onClick={() => { void products.refetch(); }} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft">
              Retry
            </button>
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-12 text-center">
            <span className="rounded-full bg-soft p-3 text-ink-3"><Package className="h-5 w-5" /></span>
            <p className="font-semibold text-ink">{search ? "No products match your search" : "No products yet"}</p>
            {!search && <p className="text-sm text-ink-2">Create the first product for accounts to see.</p>}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left font-mono text-[10px] uppercase tracking-wider text-ink-3">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Description</th>
                <th className="px-4 py-3 text-right font-semibold">Price / min</th>
                <th className="px-4 py-3 text-right font-semibold">Range</th>
                <th className="w-12 px-4 py-3" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visible.map((product) => (
                <tr key={product.id} className="group border-b border-line last:border-0 hover:bg-soft/50">
                  <td className="px-4 py-3 font-medium text-ink">{product.name}</td>
                  <td className="max-w-md truncate px-4 py-3 text-ink-2" title={product.description ?? undefined}>
                    {product.description || <span className="text-ink-3">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-ink">{rupees(product.price)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-ink-2">
                    {isPlan(product) ? amountRange(product) : <span className="text-ink-3">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <ActionMenu
                      ariaLabel={`Product ${product.name}`}
                      items={[
                        { label: "Edit", onClick: () => setDialog({ kind: "edit", product }) },
                        { label: "Delete", danger: true, onClick: () => setDialog({ kind: "delete", product }) },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {products.hasNextPage && (
          <div className="border-t border-line p-3 text-center">
            <button
              onClick={() => { void products.fetchNextPage(); }}
              disabled={products.isFetchingNextPage}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-brand hover:bg-brand-soft disabled:cursor-wait disabled:text-ink-3"
            >
              {products.isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>

      {dialog?.kind === "create" && (
        <ProductFormModal saving={m.create.isPending} onSubmit={create} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === "edit" && (
        <ProductFormModal
          product={dialog.product}
          saving={m.update.isPending}
          onSubmit={(input) => update(dialog.product, input)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "delete" && (
        <ConfirmModal
          title="Delete product?"
          body={`"${dialog.product.name}" will be deleted for every account. This cannot be undone.`}
          confirmLabel="Delete"
          onClose={() => setDialog(null)}
          onConfirm={() => remove(dialog.product)}
        />
      )}
    </div>
  );
}
